import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigation } from '../../contexts/NavigationContext';
import { COLORS, BORDER_RADIUS, CHAT_TOKENS } from '../../styles/UISystem';
import { ButtonRounded } from '../UISystem/Buttons';
// import { HeaderAnonymous } from '../UISystem/Headers/HeaderAnonymous'; // Temporarily commented out
import { HeaderConnectionImage } from '../UISystem/Headers';
import { ChatInputNormal } from '../UISystem/Chat/ChatInputNormal';
import { IconCircleButton } from '../UISystem/Icons/IconCircleButton';
import { Plus } from '@phosphor-icons/react/dist/ssr/Plus';
import { ChatMessage } from '../UISystem/Chat/ChatMessage';
import { ContactShareBubble } from '../UISystem/Chat/ContactShareBubble';
import { ContactCardBubble } from '../UISystem/Chat/ContactCardBubble';
import { AnonNameCardMorph } from '../UISystem/Chat/AnonNameCardMorph';
import { InviteSentNotice } from '../UISystem/Chat/InviteSentNotice';
import { ShareLineBubble } from '../UISystem/Chat/ShareLineBubble';
import ChatImageBubble from '../UISystem/Chat/ChatImageBubble';
import { formatMetSentence, formatBannerDateTimeLine } from '../../utils/locationFormatters';
import { getDateDividerLabel, findGreetingDoorId } from '../../utils/chatHelpers';
import { anonCardName } from '../../utils/anonCardName';
// import { LinkableContactItem } from '../UISystem/Chat/LinkableContactItem'; // Removed unused import
// import { FormConnection } from '../UISystem/Forms/FormConnection'; // Removed unused import
import { sendMessage } from '../../services/chat/chatService';
import { buildSentChatMessage } from '../../services/chat/messageBuilders';
import { AnimatePresence, motion } from 'framer-motion';
import { getSenderId, getProfileUserId, isOwnSenderId } from '../../utils/identityUtils';
import { isPWA } from '../../utils/installPrompt';

// Phase 4: Subtle entrance animation for chat messages
const messageAnimation = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.15, ease: 'easeOut' },
};

// Same fade+slide entrance as messageAnimation, but variant-named so a child (the
// contact card's icon) can inherit the enter state and pop in. Gated by the list's
// <AnimatePresence initial={false}> exactly like every message — plays on land,
// never on chat reopen. Used only by ContactCardBubble (the Profile contact card).
const contactCardAnimation = {
  initial: 'hidden',
  animate: 'visible',
  variants: {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0 },
  },
  transition: { duration: 0.15, ease: 'easeOut' },
};

/**
 * Shared UI components for the connection page
 * Used by both ProfileConnectionPage and AnonConnectionPage
 *
 * IMPORTANT: This component has four main sections:
 *
 * 0. SHARED HEADER (Common to all views):
 *    - Contains the header with profile name and back button
 *    - Present regardless of user type or data state
 *
 * 1. ANONYMOUS USER VIEW (isAnonymousUser=true):
 *    - Used when an anonymous user views connection details
 *    - Shows profile user's shared contact info and connection metadata
 *
 * 2. PROFILE USER VIEW - METADATA ONLY (isAnonymousUser=false):
 *    - Used when a profile user views connection details but anonymous user hasn't shared data yet
 *    - Shows only connection metadata (date/time, location) and a waiting message
 *
 * 3. PROFILE USER VIEW - COMPLETE (isAnonymousUser=false):
 *    - Used when a profile user views connection details after anonymous user has shared data
 *    - Shows anonymous user's shared contact info and connection metadata
 *
 * These views have different layouts and behaviors.
 * When modifying this component, always be clear about which section you're changing.
 */
export const ConnectionPageShared = ({
  // Separate data objects
  profileData = {},
  // Action handlers
  onAddToContacts,
  onGoBack,
  showBackButton = true,
  onImageClick, // Handler for profile image click
  // Display options
  showAddToContacts = true,
  // Loading state
  isLoading = false,
  chatRoomId = null,
  isAnonymousUser = false,
  // Custom spacer height for chat messages - removed as no longer needed
  // customSpacerHeight = null,
  // Allow passing in actualChatMessages and actualSetChatMessages from parent
  actualChatMessages: externalChatMessages = null,
  actualSetChatMessages: externalSetChatMessages = null,
  // Custom input component to override default chat input
  customInputComponent = null,
  threadHeader = false,
  // THREAD's connection-count badge, rendered inside the back group
  headerBackBadge = null,
  hideMeetingBanner = false,
  // Profile name editing props
  profileName = '',
  // Share contact handler for profile users
  onShareContact = null,
  // Take photo handler — camera icon in chat input
  onTakePhoto = null,
  // Get my QR button props (for anon users)
  showGetOwnQRButton = false,
  onGetOwnQRClick = null,
  // Chat input placeholder - allows custom text like "What you spoke about" for new connections
  chatPlaceholder = 'Message',
  // Sub-copy line under the composer (Anon expiry framing, social-track #11):
  // "Chat expires in 3 days" / tappable "Join Royale to keep your chat"
  composerSubline = null,
  // Fires after a chat message sends (the Profile's one-send talk prompt)
  onChatMessageSent = null,
  // Connection status — 'deleted'/'confirmed'/'offline' for banner logic
  connectionStatus = null,
  // Activity status — { text, isActive } from useActivityStatus
  activityStatus = null,
  // Location display — "Neighborhood, City" string for live location
  locationDisplay = null,
  // Invite to Royale button props (Profile users only)
  showInviteButton = false,
  onInviteToRoyale = null,
  inviteSent = false,
  // Optional override for system message click (e.g., open LocationOverlay directly)
  onSystemMessageClick: onSystemMessageClickProp = null,
  // Share pill element — rendered above chat input in footer (Profile users only)
  sharePillElement = null,
  // Docked CTA banner element (WelcomeInviteBanner) — Anon, rendered above chat input
  footerBanner = null,
  // Show plus button outside chat input (after pill dismissed or already shared)
  showPlusButton = false,
  // Inactive Anon banner — rendered above chat input
  inactiveBanner = null,
  // Inactive status — used to disable chat input for fully inactive connections
  inactiveStatus = null,
  // Plus button CTA mode — black circle when exchange is incomplete
  plusButtonProminent = false,
  // Callback when the Anon taps their own name bubble (opens the edit overlay —
  // the greeting-as-name recovery)
  onAnonCardClick = null,
  // The Anon's stored name, passed ONLY once their contact value has committed.
  // null = no contact on the record, so the name bubble stays plain speech; any
  // string (including '') flips it into the contact card via AnonNameCardMorph
  // (user design 2026-08-20).
  //
  // PASSED BY BOTH PAGES, from the same predicate on each side's own copy of
  // the record — the Anon's `hasSharedContact`, the host's
  // `anonData.sharedFields.length`. It cannot be derived from the message
  // stream here: the host's view strips every `type: 'contact'` message on
  // mount (see the filter effect below), so the authoritative write is not in
  // `actualChatMessages` on that side.
  anonContactCardName = null,
  // Venue confirm affordance (Profile view, post-share, unconfirmed venue):
  // { venueName, onConfirm } — the banner shows the ungated suggested venue
  // underlined with a floating check circle; null = no affordance
  venueConfirmAffordance = null,
  // Room metadata — feeds the sentence banner's in/at derivation (D6). The
  // persisted system-message content stays the bare place string; the
  // preposition is named at render time by matching against this.
  connectionMetadata = null,
  // Landing → chat reveal (2026-07-27 user call): the first N messages land
  // one after another (the Profile "speaks") instead of dumping at once.
  // A COUNT owned by the tap site (the batch the person saw waiting); 0 =
  // no stagger. Messages beyond the count keep the normal pop-on-land.
  entranceStaggerCount = 0,
}) => {
  // ===== SHARED FUNCTIONALITY - CHAT STATE =====
  // These states and refs are used by both anonymous and profile users for chat functionality
  const [chatInputValue, setChatInputValue] = useState('');

  // Prompt chips state - DISABLED: causing user confusion, needs redesign
  // TODO: Redesign conversation starter chips before re-enabling
  const showPromptChips = false;
  const messageChips = ['Great to talk about', 'Remember to', 'I recommend'];
  const chatInputRef = useRef(null);

  // Use external chat messages if provided, otherwise use internal state
  const [internalChatMessages, setInternalChatMessages] = useState([]);
  const actualChatMessages = externalChatMessages || internalChatMessages;
  const actualSetChatMessages = externalSetChatMessages || setInternalChatMessages;

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const prevMessageId = useRef('');

  // Get navigation context for system message tap → detail page
  const { navigateTo } = useNavigation();

  // Handler for clicking on system messages - parent can override (e.g., open LocationOverlay)
  const handleSystemMessageClick = useCallback(() => {
    if (onSystemMessageClickProp) {
      console.log('[CONNECTION_PAGE_SHARED] System message clicked, calling parent handler');
      onSystemMessageClickProp();
    } else {
      const detailPage = isAnonymousUser ? 'anon-connection-detail' : 'profile-connection-detail';
      console.log(`[CONNECTION_PAGE_SHARED] System message clicked, navigating to ${detailPage}`);
      navigateTo(detailPage, { source: 'system-message' });
    }
  }, [isAnonymousUser, navigateTo, onSystemMessageClickProp]);

  // Handler for clicking on contact card bubble - navigates to detail page
  // Determine the current user type for chat message handling
  const currentUserType = isAnonymousUser ? 'anon' : 'profile';

  // Sidedness = "was this ever MY id?", not equality with the current id —
  // and this belt is load-bearing, not redundant with deserializeMessage's
  // normalization: the cache-first instant render and in-memory optimistic
  // messages never pass deserialize, so pre-mint cached rows reach here
  // still carrying the anon id (f/chat-sidedness).
  const isSentMessage = useCallback((msg) => isOwnSenderId(msg.senderId), []);

  const handleContactCardClick = useCallback(
    (msg) => {
      // Profile user tapping their own sent card
      if (isSentMessage(msg)) {
        if (onShareContact) {
          // Peer online → open sharing overlay to update
          console.log(
            '[CONNECTION_PAGE_SHARED] Profile tapped own contact card, opening share overlay'
          );
          onShareContact();
        } else {
          // Peer offline → inactive (can't reshare without delivery channel)
          console.log(
            '[CONNECTION_PAGE_SHARED] Profile tapped own contact card, peer offline — no action'
          );
        }
        return;
      }
      // Other user's card → navigate to detail page
      const detailPage = isAnonymousUser ? 'anon-connection-detail' : 'profile-connection-detail';
      console.log(`[CONNECTION_PAGE_SHARED] Contact card clicked, navigating to ${detailPage}`);
      navigateTo(detailPage, { source: 'contact-card' });
    },
    [isAnonymousUser, navigateTo, onShareContact, isSentMessage]
  );

  // Sort all messages chronologically. Filter per-user system messages
  // so each user only sees their own (old-format system messages show to everyone).
  const sortedChatMessages = useMemo(() => {
    const userId = getProfileUserId();
    return actualChatMessages
      .filter((msg) => {
        if (msg.type !== 'system') return true;
        // Per-user format: show only own messages
        if (userId && chatRoomId && msg.id?.startsWith(`system_${chatRoomId}_`)) {
          return msg.id.endsWith(`_${userId}`);
        }
        // Old format (system_${chatRoomId}): show to everyone
        return true;
      })
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }, [actualChatMessages, chatRoomId]);

  // Entrance-stagger delays, one O(n) pass memoized (/simplify — was a per-
  // message slice/filter on every render): non-system messages in the tap
  // batch delay by speaking order × the shared beat; system pill is chrome —
  // instant. Values in SECONDS (framer transition units); null = no stagger.
  const staggerDelays = useMemo(() => {
    if (!entranceStaggerCount) return null;
    let order = 0;
    return sortedChatMessages.map((m, i) => {
      if (i >= entranceStaggerCount || m?.type === 'system') return 0;
      return (order++ * CHAT_TOKENS.MESSAGE_BEAT_MS) / 1000;
    });
  }, [sortedChatMessages, entranceStaggerCount]);

  // Date divider logic lives in chatHelpers (getDateDividerLabel) — pure and
  // tested there. Same format as system messages: "Mon 12:55pm".
  const getDateDivider = (msg, index) => getDateDividerLabel(sortedChatMessages, index);

  // Greeting door (Anon view) — the ONE chat bubble that routes to the detail
  // page under the contract #9a end-state. Pure rule, tested in chatHelpers.
  const greetingDoorId = useMemo(
    () => (isAnonymousUser ? findGreetingDoorId(sortedChatMessages, isSentMessage) : null),
    [isAnonymousUser, sortedChatMessages, isSentMessage]
  );

  // Renders a date divider element
  const DateDivider = ({ label }) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        padding: '12px 0 8px',
        width: '100%',
      }}
    >
      <span
        style={{
          fontSize: '13px',
          fontWeight: '400',
          color: '#8E8E93',
        }}
      >
        {label}
      </span>
    </div>
  );

  // ===== SINGLE MESSAGE RENDERER =====
  // One taxonomy for both views — extracted from twin ~140-line map bodies
  // (refactor log row; the social-track share lines would have made a third
  // drifted copy). View differences stay explicit inside: invite handling,
  // name-bubble tap, system-pill click + venue affordance.
  const renderMessage = (msg, index) => {
    const dateDivider = getDateDivider(msg, index);
    const sent = isSentMessage(msg);
    const staggered = staggerDelays?.[index] || 0;
    const wrap = (children, animation = messageAnimation) => (
      <React.Fragment key={msg.id}>
        {dateDivider && <DateDivider label={dateDivider} />}
        <motion.div
          {...animation}
          {...(staggered > 0 ? { transition: { ...animation.transition, delay: staggered } } : {})}
        >
          {children}
        </motion.div>
      </React.Fragment>
    );

    // Share lines — shares as speech (social-track #13). New exchanges only;
    // links act on the received side. Contract #9a end-state (2026-07-21):
    // values act DIRECTLY on both sides — tap the number, get the phone. Only
    // the "My contact" phrase doors to the detail page (no single value).
    if (msg.type === 'contact_share' && msg.speech) {
      return wrap(
        <ShareLineBubble
          shareType={msg.shareType}
          shareValue={msg.shareValue}
          contactLink={msg.contactLink}
          isSent={sent}
          disableLinks={sent}
          onContactClick={() => handleContactCardClick(msg)}
        />
      );
    }

    // LEGACY: historic card-style contact cards keep rendering as cards
    if (msg.type === 'contact_share' && msg.cardStyle) {
      return wrap(
        <ContactCardBubble
          name={msg.profileName}
          isSent={sent}
          onClick={() => handleContactCardClick(msg)}
          sharedContactTypes={msg.sharedContactTypes}
          profileData={msg.profileData}
        />,
        contactCardAnimation
      );
    }

    // LEGACY: historic inline contact entries (pre-May-2026 Anon shares)
    if (msg.type === 'contact_share' && msg.contacts) {
      return wrap(<ContactShareBubble contacts={msg.contacts} isSent={sent} disableLinks={sent} />);
    }

    // Invite/welcome CTAs: the Anon sees the docked WelcomeInviteBanner (never
    // in-stream — including stale isWelcomeCta cards from earlier builds);
    // the Profile renders only their own sent invite, as the InviteSentNotice
    // (a *received* invite here only happens with stale cross-type data and
    // must never read "you sent it").
    if (msg.isInviteCta || msg.isWelcomeCta) {
      if (isAnonymousUser || msg.isWelcomeCta || !sent) return null;
      return (
        <React.Fragment key={msg.id}>
          {dateDivider && <DateDivider label={dateDivider} />}
          <InviteSentNotice inviteeName={profileData?.name} timestamp={msg.timestamp} />
        </React.Fragment>
      );
    }

    // Photos
    if (msg.contentType === 'image') {
      return wrap(
        <ChatImageBubble
          url={msg.content}
          isSent={sent}
          uploading={msg.uploading}
          uploadFailed={msg.uploadFailed}
          onRetry={msg.onRetry}
        />
      );
    }

    // Classified Anon share bubble — verbatim speech with the link riding
    // along for the receiving side (device pass 1: "contacts aren't coming
    // through linked")
    if (msg.type === 'chat' && msg.shareType && msg.shareValue) {
      return wrap(
        <ShareLineBubble
          verbatimText={typeof msg.content === 'string' ? msg.content : msg.shareValue}
          shareType={msg.shareType}
          shareValue={msg.shareValue}
          isSent={sent}
          disableLinks={sent}
        />
      );
    }

    // Name message. It MORPHS into a contact card once the Anon's contact value
    // commits (user design 2026-08-20, reinstating the conversion that
    // shares-as-speech had retired). That morph replaced ThreadConnectedCard —
    // the confirmation happens to the artifact in the thread rather than on a
    // panel over it — and it leaves both sides mirroring each other: one card
    // each. The contact VALUE no longer bubbles at all; it lives behind the tap.
    //
    // BOTH SIDES, not just the Anon's (corrected same day). The first pass held
    // the host's side back to preserve a "verbatim greeting", which under
    // THREAD does not exist: the greeting-shape composer is gated
    // `!isThreadVariant()`, and the Name field submits with extraction OFF, so
    // content === anonName and the preserved bubble said what the card says.
    //
    // bubbleIsJustTheName is the exact condition, not a variant check: the
    // bubble carries something a card cannot ONLY when the typed words differ
    // from the stored name. That is a CHAT-variant greeting ("Hi it's Franklin"
    // → anonName "Franklin"), which keeps its speech bubble on both sides. A
    // bare name typed into that same composer extracts to null, so it equals
    // the record and correctly becomes a card there too.
    //
    // Either way the Anon's tap → edit overlay survives (the greeting-as-name
    // recovery, social-track Q12) — on BOTH halves, since a name can be
    // corrected before a contact is ever shared.
    if (msg.anonName) {
      const nameTap = sent && isAnonymousUser && onAnonCardClick ? onAnonCardClick : undefined;
      const bubbleText = typeof msg.content === 'string' ? msg.content : msg.anonName;
      const bubbleIsJustTheName = bubbleText === msg.anonName;

      if (bubbleIsJustTheName) {
        return wrap(
          <AnonNameCardMorph
            text={bubbleText}
            // Card label by side — own card uses its own bubble name, received
            // uses the peer name (§3 fix). Why lives in anonCardName's JSDoc.
            cardName={anonCardName({ sent, anonContactCardName, anonName: msg.anonName })}
            // null vs empty string, deliberately: null means "no contact on the
            // record", '' means "on the record but the name hasn't landed".
            // A truthiness check here would have collapsed the second into the
            // first and left the card hidden on a nameless share.
            showCard={anonContactCardName !== null}
            isSent={sent}
            onSpeechClick={nameTap}
            // The Anon's own card edits their share; the host's tap on the
            // Anon's card is a received card like any other — detail page, via
            // the same handler the host's own contact cards use.
            onCardClick={nameTap || (() => handleContactCardClick(msg))}
          />
          // messageAnimation, NOT contactCardAnimation: the row ENTERS as a
          // speech bubble and only becomes a card later. The card's icon pop is
          // driven by the morph's own variants at the flip, so it doesn't need
          // the variant-named entrance the host's card uses.
        );
      }

      return wrap(
        <div onClick={nameTap} style={nameTap ? { cursor: 'pointer' } : undefined}>
          <ChatMessage message={bubbleText} type={sent ? 'sent' : 'received'} />
        </div>
      );
    }

    // THREAD holds the MOMENT BANNER until the host has answered the venue
    // sheet — a GPS guess shown and then corrected reads as a wrong fact
    // (caller owns the derivation and its escapes).
    //
    // Gated on meetingDate, not on type alone: buildSystemMeetingMessage is
    // the only system-message producer today, so `type === 'system'` was
    // equivalent — and would have silently swallowed the FIRST additional
    // system message anyone added, under main, with no failing test
    // (/simplify altitude pass 2026-08-17).
    if (msg.type === 'system' && msg.meetingDate && hideMeetingBanner) return null;

    // System pill + regular chat. While the venue confirm affordance is active
    // (Profile view only), the banner shows the ungated suggested venue.
    const showVenueAffordance =
      !isAnonymousUser && msg.type === 'system' && venueConfirmAffordance?.venueName;
    let messageContent;
    if (msg.type === 'system' && msg.meetingDate) {
      // Sentence banner (D6; unified at device pass 1, 2026-07-27, user):
      // "Met at {venue}" / "Met in {neighborhood}" over the quiet "Mon,
      // 9:20pm" line — the SAME two-line pill both sides ("the Profile one is
      // far better, and it's still lightweight"). Composed at render from the
      // persisted bare place + metadata. Supersedes the Anon sentence-only
      // scoping and the "Met Friday" floor; no-place rooms show the date line
      // alone on both sides.
      const place = showVenueAffordance ? venueConfirmAffordance.venueName : msg.content;
      const placeSentence = formatMetSentence(place, connectionMetadata);
      const dateLine = formatBannerDateTimeLine(msg.meetingDate, msg.meetingUtcOffset);
      messageContent = [placeSentence, dateLine].filter(Boolean).join('\n');
    } else {
      messageContent =
        typeof msg.content === 'string'
          ? msg.content
          : msg.content
            ? JSON.stringify(msg.content)
            : 'Message content unavailable';
    }

    // Anon side doors (contract #9a end-state): the trio is the header, the
    // GREETING bubble, and the venue banner. Plain chat bubbles have no door —
    // only the greeting (identified by findGreetingDoorId) taps to the card.
    // Linkified segments inside a bubble keep their own action (stopPropagation).
    const anonBubbleTap =
      isAnonymousUser && !sent && msg.type !== 'system' && msg.id && msg.id === greetingDoorId
        ? () => navigateTo('anon-connection-detail', { source: 'chat-bubble' })
        : undefined;

    const bubble = (
      <ChatMessage
        message={messageContent}
        type={msg.type === 'system' ? 'system' : sent ? 'sent' : 'received'}
        onClick={msg.type === 'system' ? handleSystemMessageClick : undefined}
        // D6: the sentence replaced the pin's job (mocks drop it both sides —
        // the pill reads as content now, not a location datum)
        showMapPin={false}
        onConfirmVenue={showVenueAffordance ? venueConfirmAffordance.onConfirm : null}
      />
    );

    return wrap(
      anonBubbleTap ? (
        <div onClick={anonBubbleTap} style={{ cursor: 'pointer' }}>
          {bubble}
        </div>
      ) : (
        bubble
      )
    );
  };

  // ===== SHARED FUNCTIONALITY - AUTO-SCROLL =====
  // Auto-scroll for new messages while keeping system message visible
  useEffect(() => {
    // Only run this effect when a new message is added
    const regularMessages = actualChatMessages.filter((msg) => msg.type !== 'system');

    // Only scroll if there are regular messages and a new one was added
    if (regularMessages.length > 0) {
      const latestMessage = regularMessages[regularMessages.length - 1];

      // Check if this is a new message we haven't scrolled to yet
      if (latestMessage.id !== prevMessageId.current) {
        prevMessageId.current = latestMessage.id;

        // Scroll to the end of messages after a brief delay for DOM to settle
        // This prevents the "blink and shift" when messages appear
        requestAnimationFrame(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
          }
        });
      }
    }
    // No need to handle system messages separately as they're now in a fixed position
  }, [actualChatMessages]);

  // ===== SHARED FUNCTIONALITY - CHAT INPUT HANDLERS =====
  // These handlers manage the chat input for both anonymous and profile users

  // Update the chat input value as the user types
  const handleChatInputChange = (value) => {
    setChatInputValue(value);
  };

  // Handle prompt chip click - append text to input and focus
  const handleChipClick = (chipText) => {
    setChatInputValue((prev) => {
      // Append with space if there's existing text, add trailing space for typing
      if (prev.trim().length > 0) {
        const prefix = prev.endsWith(' ') ? prev : `${prev} `;
        return `${prefix}${chipText} `;
      }
      return `${chipText} `;
    });
    // Focus input to bring up keyboard for finishing the sentence
    if (chatInputRef.current && chatInputRef.current.focus) {
      chatInputRef.current.focus();
    }
  };

  // Send a chat message via WebSocket
  // Used by both anonymous and profile users
  const handleSendChatMessage = useCallback(
    async (message) => {
      if (!message.trim() || !chatRoomId) return;

      try {
        // Build message once — used for both local state and WebSocket send
        const currentSenderId = getSenderId(); // Phase 1: Universal identity for sent/received logic
        const messageData = buildSentChatMessage({
          senderType: currentUserType,
          senderId: currentSenderId,
          content: message.trim(),
        });

        // Add to local messages immediately for UI responsiveness
        // This creates the appearance of instant messaging
        actualSetChatMessages((prevMessages) => [...prevMessages, messageData]);

        // Clear input field after sending
        setChatInputValue('');

        // Send to chat service via WebSocket
        await sendMessage(chatRoomId, messageData);
        onChatMessageSent?.();
      } catch (error) {
        console.error('[CHAT] Error sending message:', error);
      }
    },
    [chatRoomId, currentUserType, actualSetChatMessages, onChatMessageSent]
  );

  // ===== SHARED FUNCTIONALITY - FORMATTING =====
  // We use formatConnectionMessage from locationFormatters.js
  // This utility formats both location and date/time information consistently
  // Format follows: "Connected in [Neighborhood] @ [Venue], [Address] // [Day] [Month] [Date] ([Time])"

  // Note: Scroll-to-bottom is handled by the auto-scroll useEffect above (line ~117)
  // which tracks individual message IDs to avoid duplicate scrolls

  // Dismiss prompt chips when navigating away from this page
  useEffect(() => {
    return () => {
      // On unmount, set the flag to 'dismissed' so chips don't reappear on return
      if (chatRoomId && showPromptChips) {
        sessionStorage.setItem(`prompt_chips_active_${chatRoomId}`, 'dismissed');
      }
    };
  }, [chatRoomId, showPromptChips]);

  // Filter out any contact messages from the chat messages when component mounts
  useEffect(() => {
    // Only run for profile user view since we don't want contact messages in chat
    if (!isAnonymousUser && actualChatMessages.length > 0) {
      const hasContactMessages = actualChatMessages.some((msg) => msg.type === 'contact');

      if (hasContactMessages) {
        console.log('[CHAT] Filtering out contact messages from chat display');
        actualSetChatMessages((prevMessages) =>
          prevMessages.filter((msg) => msg.type !== 'contact')
        );
      }
    }
  }, [isAnonymousUser, actualChatMessages, actualSetChatMessages]);

  // We've removed the automatic system message insertion here
  // The date/time/location system message will now be added by AnonConnectionPage.js
  // when the anonymous user submits their contact information

  // We've consolidated auto-scrolling into a single effect above
  // No additional auto-scrolling needed here

  // ===== PWA KEYBOARD DETECTION =====
  // In PWA mode, drop safe-area bottom padding when keyboard is open —
  // keyboard covers the home indicator, so the padding creates a gap.
  // Scoped to PWA only — browser mode has no gap (toolbar fills the space).
  const initialInnerHeight = useRef(window.innerHeight);
  const [pwaKeyboardOpen, setPwaKeyboardOpen] = useState(false);

  useEffect(() => {
    if (!isPWA()) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => {
      setPwaKeyboardOpen(vv.height < initialInnerHeight.current - 100);
    };
    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, []);

  // Shared bottom offset — messages container and floating elements must stay in sync.
  // 80px clears the standard 60px input + footer padding with the same ~12px gap
  // the floating pill reads best at (device-tuned at checkpoint B — 70px let the
  // pill bump the input top).
  const safeAreaBottom = pwaKeyboardOpen ? '0px' : 'env(safe-area-inset-bottom)';
  const contentBottomOffset = `calc(80px + ${safeAreaBottom})`;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100vh',
        backgroundColor: COLORS.background,
        position: 'relative',
        overflow: 'hidden',
        paddingTop: '8px',
        paddingBottom: '16px',
      }}
    >
      {/* Debug: Show last 4 chars of chatRoomId */}
      {/* {chatRoomId && (
        <div
          style={{
            position: 'absolute',
            top: '4px',
            right: '8px',
            fontSize: '11px',
            color: '#999',
            fontFamily: 'monospace',
            zIndex: 1000,
          }}
        >
          Room: {chatRoomId.slice(-4)}
        </div>
      )} */}

      {/* ===== SHARED COMPONENT: HEADER CONTAINER ===== */}
      {/* Now using our standardized HeaderConnection component */}
      <div
        style={{
          width: '100%',
          position: 'fixed',
          top: '-2px', // Moved up from 8px to -2px (10px upward movement)
          left: 0,
          right: 0,
          zIndex: 10,
          borderBottom: 'none', // No divider line for either user type
        }}
      >
        {isAnonymousUser ? (
          /* OPTIMIZATION: Always show HeaderConnectionImage for anon users
             When profileData is null, shows grey circle placeholder - no blank screen */
          <>
            <HeaderConnectionImage
              name={profileData?.name || ''}
              imageUrl={profileData?.photoUrl || ''}
              showPlaceholder={true}
              showBackButton={showBackButton}
              onBackClick={onGoBack}
              onImageClick={onImageClick}
              activityStatus={activityStatus}
              locationDisplay={locationDisplay}
              // Anon simplicity (exchange-polish items 2/3): "{Name} ›" is the
              // visible door; a green dot when active now, no status text ever
              dotOnly={true}
              // THREAD drops the chevron (no reference frame shows one); the
              // photo+name group still carries the tap to Detail.
              doorChevron={!threadHeader}
              alignToChatRule={threadHeader}
              backBadge={headerBackBadge}
            />
            {/* Clear link moved into HeaderConnectionImage */}
            {/* Get my QR button - positioned in top right */}
            {showGetOwnQRButton && onGetOwnQRClick && (
              <div style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 11 }}>
                <ButtonRounded
                  text="Get my QR"
                  size="compact"
                  onClick={onGetOwnQRClick}
                  style={{ backgroundColor: '#D4EDDA', color: '#155724', fontSize: '14px' }}
                />
              </div>
            )}
          </>
        ) : (
          /* For profile users: HeaderConnectionImage with peer name (photo only if peer has one — PXP).
             Unnamed = empty title (the "Hello" greeting retired 2026-07-19 — it
             added nothing); the name snaps in when the share-back lands. */
          <HeaderConnectionImage
            name={profileName || ''}
            imageUrl={profileData?.photoUrl || ''}
            showPlaceholder={true}
            namePlaceholder=""
            showBackButton={true}
            onBackClick={onGoBack}
            onImageClick={onImageClick}
            activityStatus={activityStatus}
            locationDisplay={locationDisplay}
            showInviteButton={showInviteButton}
            onInviteToRoyale={onInviteToRoyale}
            inviteSent={inviteSent}
          />
        )}
      </div>

      {/* ===== SHARED COMPONENT: MAIN SCROLLABLE CONTENT CONTAINER ===== */}
      {/* Scrolls from status bar to above chat input, content goes under frosted header */}
      {!isLoading && (
        <div
          ref={messagesContainerRef}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: contentBottomOffset,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            zIndex: 1 /* Below footer (zIndex: 10) so pill/buttons are clickable */,
          }}
        >
          <div
            style={{
              width: '90%',
              marginLeft: 'auto',
              marginRight: 'auto',
              paddingTop:
                '70px' /* Compact single-row header (~54px) + gap — test on device, adjust */,
              paddingBottom: '3px' /* Small gap above chat input */,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* ===== ANONYMOUS USER VIEW ===== */}
            {/* This entire section is only shown when isAnonymousUser=true */}
            {/* It displays the chat interface and profile user's information */}
            {isAnonymousUser && (
              <React.Fragment>
                {/* ===== ANONYMOUS USER COMPONENT: MESSAGE LIST ===== */}
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* All message types in a single list. initial flips ON for
                      the landing→chat reveal so the mount batch can play its
                      staggered entrance; every other mount stays initial=false
                      (no animation replay on reopen). */}
                  <AnimatePresence initial={entranceStaggerCount > 0}>
                    {sortedChatMessages.map(renderMessage)}
                  </AnimatePresence>
                  <div ref={messagesEndRef} style={{ height: '10px' }} />
                </div>

                {/* Chat input and buttons will be moved to fixed footer */}

                {/* Date and location is now shown only in the chat messages as a system message */}
              </React.Fragment>
            )}

            {/* ===== PROFILE USER VIEW ===== */}
            {/* This entire section is only shown when isAnonymousUser=false */}
            {/* It displays the chat interface for profile users */}
            {!isAnonymousUser && (
              <React.Fragment>
                {/* ===== PROFILE USER COMPONENT: MESSAGE LIST ===== */}
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* All message types in a single list */}
                  <AnimatePresence initial={false}>
                    {sortedChatMessages.map(renderMessage)}
                  </AnimatePresence>

                  {/* Link request UI moved to header - see HeaderConnectionImage */}

                  <div ref={messagesEndRef} style={{ height: '10px' }} />
                </div>
              </React.Fragment>
            )}
          </div>
        </div>
      )}

      {/* ===== SHARED COMPONENT: LOADING STATE ===== */}
      {/* Shows a loading indicator when data is being fetched */}
      {isLoading && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '50vh',
            width: '100%',
          }}
        >
          {/* No loading text to ensure smooth animation */}
        </div>
      )}

      {/* ===== FLOATING ELEMENTS ABOVE FOOTER ===== */}
      {/* These float over messages, not part of footer height */}
      {/* Only render when there's content — empty div creates visual gap */}
      {!isLoading &&
        (sharePillElement || inactiveBanner || connectionStatus === 'deleted' || footerBanner) && (
          <div
            style={{
              position: 'fixed',
              bottom: contentBottomOffset,
              left: 0,
              right: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              padding: '0 16px 8px',
              pointerEvents: 'none',
              zIndex: 10,
            }}
          >
            {/* Inactive/deleted banner — frosted pill above chat input */}
            {(() => {
              let bannerText = null;
              if (connectionStatus === 'deleted') {
                const peerName = profileName || profileData?.name || '';
                const firstName = peerName ? peerName.split(' ')[0] : 'This person';
                bannerText = `${firstName} is no longer active`;
              } else if (inactiveBanner) {
                bannerText = inactiveBanner;
              }
              if (!bannerText) return null;
              return (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <span
                    style={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#8E8E93',
                      backgroundColor: 'rgba(255, 255, 255, 0.85)',
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                      borderRadius: '16px',
                      padding: '8px 16px',
                    }}
                  >
                    {bannerText}
                  </span>
                </div>
              );
            })()}

            {/* Share pill — Profile users, rendered above chat input */}
            {sharePillElement && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  pointerEvents: 'auto',
                }}
              >
                {sharePillElement}
              </div>
            )}

            {/* Docked welcome/invite CTA banner (Anon) — self-animates via internal
              AnimatePresence, so it's always mounted here and slides itself in/out. */}
            {footerBanner}
          </div>
        )}

      {/* ===== FIXED FOOTER WITH BUTTONS AND CHAT INPUT ===== */}
      {/* This container is fixed at the bottom of the screen */}
      {/* Background is transparent - individual elements add their own backgrounds */}
      {!isLoading && (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'transparent',
            padding: '16px',
            paddingBottom: `calc(16px + ${safeAreaBottom})`,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            zIndex: 10,
            pointerEvents: 'none', // Allow clicks to pass through to chat
          }}
        >
          {/* Action Buttons - only render if there are buttons to show */}
          {((isAnonymousUser && showAddToContacts) || (!isAnonymousUser && showAddToContacts)) && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                width: '100%',
                alignItems: 'center',
                pointerEvents: 'auto', // Enable clicks on buttons
              }}
            >
              {/* Add to contacts button - for anonymous users */}
              {isAnonymousUser && showAddToContacts && (
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '16px',
                  }}
                >
                  <ButtonRounded
                    text="Save to contacts"
                    variant="secondary"
                    size="large"
                    onClick={onAddToContacts}
                  />
                </div>
              )}

              {/* Add to contacts button - for profile users */}
              {!isAnonymousUser && showAddToContacts && (
                <ButtonRounded text="Add to phone" onClick={onAddToContacts} size="large" />
              )}
            </div>
          )}

          {/* Prompt Chips - shown for profile users after sharing contacts */}
          {showPromptChips && (
            <div
              style={{
                display: 'flex',
                gap: '10px',
                overflowX: 'auto',
                overflowY: 'hidden',
                width: '100%',
                paddingBottom: '4px',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                pointerEvents: 'auto', // Enable clicks on chips
              }}
            >
              {messageChips.map((chipText, index) => (
                <div
                  key={index}
                  onClick={() => handleChipClick(chipText)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 14px',
                    backgroundColor: 'transparent',
                    border: '1px solid #E0E0E0',
                    borderRadius: BORDER_RADIUS.PILL,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    fontSize: '14px',
                    fontWeight: '400',
                    color: '#AAAAAA',
                  }}
                >
                  {chipText}
                </div>
              ))}
            </div>
          )}

          {/* Chat Input */}
          <div
            style={{
              width: '100%',
              pointerEvents:
                connectionStatus === 'deleted' || inactiveStatus === 'inactive' ? 'none' : 'auto',
              opacity: connectionStatus === 'deleted' || inactiveStatus === 'inactive' ? 0.4 : 1,
            }}
          >
            {customInputComponent ? (
              // Use custom input component if provided
              customInputComponent
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                {/* initial={false}: no animation when the Plus is already there on page
                    load — it only eases in when it appears mid-session (post-share).
                    Width animates with it so the chat input slides over smoothly. */}
                <AnimatePresence initial={false}>
                  {showPlusButton && onShareContact && (
                    <motion.div
                      initial={{ width: 0, opacity: 0, scale: 0.4 }}
                      animate={{ width: 40, opacity: 1, scale: 1 }}
                      exit={{ width: 0, opacity: 0, scale: 0.4 }}
                      transition={{ duration: 0.3, ease: 'easeOut' }}
                      style={{ flexShrink: 0 }}
                    >
                      <IconCircleButton
                        icon={Plus}
                        size={20}
                        buttonSize={40}
                        iconWeight="regular"
                        iconColor={plusButtonProminent ? '#FFFFFF' : '#333'}
                        backgroundColor={plusButtonProminent ? '#000000' : '#F2F2F7'}
                        onClick={onShareContact}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <ChatInputNormal
                    ref={chatInputRef}
                    value={chatInputValue}
                    onChange={handleChatInputChange}
                    onSend={handleSendChatMessage}
                    onTakePhoto={onTakePhoto}
                    placeholder={chatPlaceholder}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Composer sub-copy — centered under the input (expiry framing) */}
          {composerSubline && (
            <div style={{ textAlign: 'center', pointerEvents: 'auto', marginTop: '-4px' }}>
              {composerSubline}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
